import { useCallback, useEffect, useRef, useState } from 'react';

interface UseLazyLoadingOptions {
  threshold?: number;
  rootMargin?: string;
  enabled?: boolean;
}

interface UseLazyLoadingReturn {
  targetRef: React.RefObject<HTMLDivElement>;
  isIntersecting: boolean;
}

/**
 * Custom hook for implementing lazy loading with Intersection Observer
 * Used for efficient pagination and data loading
 */
export const useLazyLoading = (
  onIntersect: () => void,
  options: UseLazyLoadingOptions = {}
): UseLazyLoadingReturn => {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    enabled = true,
  } = options;

  const targetRef = useRef<HTMLDivElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      setIsIntersecting(entry.isIntersecting);
      
      if (entry.isIntersecting && enabled) {
        onIntersect();
      }
    },
    [onIntersect, enabled]
  );

  useEffect(() => {
    if (!enabled) {
      return () => {};
    }

    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin,
    });

    // Observe target if it exists
    if (targetRef.current) {
      observerRef.current.observe(targetRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection, threshold, rootMargin, enabled]);

  // Separate effect to observe target when it becomes available
  useEffect(() => {
    if (!enabled || !observerRef.current || !targetRef.current) {
      return () => {};
    }

    observerRef.current.observe(targetRef.current);

    return () => {
      if (observerRef.current && targetRef.current) {
        observerRef.current.unobserve(targetRef.current);
      }
    };
  }, [enabled]);

  return {
    targetRef,
    isIntersecting,
  };
};